import React, { useState } from 'react'
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth'
import { useNavigate, Link } from 'react-router-dom'
import { FirebaseError } from 'firebase/app'
import {
  Box,
  TextField,
  Typography,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import LoginIcon from '@mui/icons-material/Login'
import { useSnackbar } from '../contexts/SnackbarContext'
import { auth } from '../firebase'
import AuthLayout from '../layouts/AuthLayout'

export const SignUpPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isEmailError, setIsEmailError] = useState(false)
  const [emailErrorText, setEmailErrorText] = useState('')
  const [isPasswordError, setIsPasswordError] = useState(false)
  const [passwordErrorText, setPasswordErrorText] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbar()

  const convertToJapanese = (errorMessage: string) => {
    switch (errorMessage) {
      case 'auth/email-already-in-use':
        return 'このメールアドレスはすでに登録されています'
      case 'auth/invalid-email':
        return 'メールアドレスの形式が正しくありません'
      case 'auth/operation-not-allowed':
        return 'この操作は許可されていません'
      case 'auth/weak-password':
        return 'パスワードは6文字以上必要です'
      default:
        return '不明なエラーが発生しました'
    }
  }

  const handleSignUp = async () => {
    if (email === '') {
      setIsEmailError(true)
      setEmailErrorText('メールアドレスを入力してください')
    } else {
      setIsEmailError(false)
      setEmailErrorText('')
    }

    if (password === '') {
      setIsPasswordError(true)
      setPasswordErrorText('パスワードを入力してください')
    } else {
      setIsPasswordError(false)
      setPasswordErrorText('')
    }

    if (email === '' || password === '') {
      return
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      await sendEmailVerification(userCredential.user)
      setDialogOpen(true)
    } catch (e: unknown) {
      if (e instanceof FirebaseError) {
        const newErrorMessage = convertToJapanese(e.code)
        showSnackbar(newErrorMessage, 'error')
      } else {
        showSnackbar('不明なエラーが発生しました', 'error')
      }
    }
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    navigate('/login')
  }

  return (
    <>
      <AuthLayout title="Chopsticksを始める" backgroundImageUrl="/sign-up.jpg">
        <TextField
          id="outlined-basic"
          label="メールアドレス"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={isEmailError}
          helperText={emailErrorText || ' '}
          fullWidth
          sx={{
            margin: '12px auto 0',
          }}
        />
        <TextField
          id="outlined-password-input"
          label="パスワード"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={isPasswordError}
          helperText={passwordErrorText || ' '}
          fullWidth
          sx={{
            margin: '12px auto 0',
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSignUp}
          size="large"
          sx={{
            width: '120px',
            fontWeight: 'bold',
            fontSize: '18px',
            margin: '16px auto',
          }}
        >
          新規登録
        </Button>

        <Box sx={{ position: 'relative', padding: 2, my: 6 }}>
          <Divider />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              px: 4,
            }}
          >
            <Typography>または</Typography>
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            mb: '12px',
          }}
        >
          アカウントは既に作成済みですか？
        </Typography>

        <Button
          variant="outlined"
          component={Link}
          to="/login"
          size="large"
          startIcon={<LoginIcon />}
          sx={{
            fontSize: '18px',
            fontWeight: 'bold',
          }}
        >
          ログイン画面へ
        </Button>
      </AuthLayout>
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
            }}
          >
            確認メールを送信しました
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            メール内のリンクをクリックし、アドレス認証を行ってください。その後、ログイン可能になります。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
